<!--
    This is the component where we are mapping the layout from one event to another event.
    This is usefull when we are trying to import contestatns from a event that has a completely different Category, Race layout, etc.

    This component will be used in the Participant.vue component

    This component will have the following props:
    - sourceEventId: The event id from where we are importing the layout

-->
<script setup lang="ts">
import { useLoaders, useTaskLoader } from '@/composeables';
import { BaseObjDao, ContestantDao, EventDao } from '@/dao';
import {
    Category,
    ContestantImportDefaultSettings,
    EventinfoBase,
    ExternalEventDatasourceResponse,
    Group,
    ImportContent,
    ImportFormat,
    IntArray,
    Race,
    RaceContestantComplete,
    type Eventinfo,
} from '@/objects/generated';
import { computed, onMounted, ref, watch } from 'vue';
import ContentComp from '../layout/ContentComp.vue';
import { FormLayout } from '../Form';
import { DropdownSelect, IconComp } from '..';
import { useCommonStore, useCommonStoreRef, useEventCommonStoreRef } from '@/stores';
import type { SelectLine, SelectLineValueType } from '@/objects';
import ButtonComp from '../Basic/ButtonComp.vue';
import { SelectLineGroup } from '@/objects/SelectLineGroup';
import { WizardContainer, WizardPageDefinition } from '@/objects/Wizard';
import WizardComp from '../Wizard/WizardComp.vue';
import FormLayoutRow from '../Form/FormLayoutRow.vue';
import InputBool from '../Basic/InputBool.vue';
import { featureIcons } from '../Menu/featureicons';
import { formatDateToInputFormat } from '@/utilities/dateutils';
import WellKnown from '@/objects/generated/static/WellKnown';
import { ImportAdditionalValues } from '../../objects/generated/classes/ImportAdditionalValues';

/**
 * the props for this component
 */
const props = defineProps<{
    sourceEventId?: number;

    externalEventData?: ExternalEventDatasourceResponse | null;
}>();

const emit = defineEmits<{
    (e: 'complete', value: ImportContent): void;
}>();

const wizardDefinition = new WizardContainer();
wizardDefinition.WizardPages = [
    new WizardPageDefinition('categories', 'racelayoutmapper.categories', featureIcons.CATEGORY),
    new WizardPageDefinition('groups', 'racelayoutmapper.groups', featureIcons.GROUPS),
    new WizardPageDefinition('settings', 'racelayoutmapper.settings', featureIcons.SETTINGS),
];

const wizard = ref<InstanceType<typeof WizardComp> | null>(null);

// We need to load the eventInfo, categories, races and groups for the sourceEvent

const { AddLoader, LoadState } = useTaskLoader();
useLoaders(AddLoader, ['RACES', 'CATEGORIES', 'GROUPS', 'CLUBS']);

const { Races, Categories, Groups } = useEventCommonStoreRef();

const { Clubs } = useCommonStoreRef();

// We create a map for the clubs so we can show the club names in the import
const clubMap = computed(() => {
    const map = new Map<number, string>();
    Clubs.value.forEach((c) => {
        map.set(c.ID, c.Name);
    });
    return map;
});

const categoryDestinationIdMap = ref<Array<SelectLineValueType>>([]);
const categorySourceIdMap = ref<Array<number>>([]);

const groupDestinationIdMap = ref<Array<SelectLineValueType>>([]);
const groupSourceIdMap = ref<Array<number>>([]);

// Contains the list of all the categories and groups that are successfully mapped this is just the IDs and since there is such a big difference between categoryIds and groupIds this should never conflict with each other in one event.
let successFullyMapped = ref<number[]>([]);

const sourceEvent = ref<Eventinfo | EventinfoBase | null>(null);
const sourceRaces = ref<Race[]>([]);
const sourceCategories = ref<Category[]>([]);
const sourceGroups = ref<Group[]>([]);

/**
 * We load all the base data for the sourceevent that we need to map all the data
 */
const loadSourceLayout = () => {
    if (!props.externalEventData && !props.sourceEventId) {
        console.error('RaceLayoutMapper: No sourceEventId or externalEventData provided');
        return;
    }

    AddLoader(async () => {
        if (props.sourceEventId) {
            sourceEvent.value = await EventDao.Event(props.sourceEventId);

            if (sourceEvent.value) {
                sourceRaces.value = await BaseObjDao.Load<Race>(Race, props.sourceEventId, []);
                sourceCategories.value = await BaseObjDao.Load<Category>(Category, props.sourceEventId, []);
                sourceGroups.value = await BaseObjDao.Load<Group>(Group, props.sourceEventId, []);
            }
        } else if (props.externalEventData) {
            sourceEvent.value = props.externalEventData.EventInfo ?? null;
            sourceRaces.value = props.externalEventData.Races ?? [];
            sourceCategories.value = props.externalEventData.Categories ?? [];
            sourceGroups.value = []; // NOTE! We dont have groups in the external data
        }

        // Fill in the categoryIdMap
        for (const cat of sourceCategories.value) {
            // We try and map using the name of the category
            let destCat = Categories.value.find((c) => c.Name.toLocaleLowerCase() === cat.Name.toLocaleLowerCase());
            categoryDestinationIdMap.value.push(destCat?.ID ?? -1); // If we find the category we map it, else we map it to -1 which mean age and gender
            categorySourceIdMap.value.push(cat.ID);

            // We add the ID to the successFullyMapped array so we can show a glow effect on the category
            if (destCat) {
                successFullyMapped.value.push(cat.ID);
            }
        }

        // Fill in the groupIdMap
        for (const group of sourceGroups.value) {
            // We try and map using the name of the group
            let destGroup = Groups.value.find((g) => g.Name.toLocaleLowerCase() === group.Name.toLocaleLowerCase());
            groupDestinationIdMap.value.push(destGroup?.ID ?? -1); // If we find the group we map it, else we map it to -1 which mean create in destination
            groupSourceIdMap.value.push(group.ID);

            if (destGroup) {
                successFullyMapped.value.push(group.ID);
            }
        }
    }, 'racelayoutmapper.loadsource');
};

/**
 * The computed datasource for the destination categories
 */
const computedDestinationCategoriesDatasource = computed(() => {
    const selectLines = Categories.value.map(
        (c) =>
            ({
                value: c.ID,
                text: c.Name,
            }) as SelectLine,
    );

    const grp1: SelectLineGroup = new SelectLineGroup('racelayoutmapper.categories', selectLines);
    const grp2: SelectLineGroup = new SelectLineGroup('', []);
    grp2.lines = [
        {
            value: -1,
            text: 'racelayoutmapper.fromagegender',
        },
        {
            value: -2,
            text: 'racelayoutmapper.createindestination',
        },
    ];

    return [grp2, grp1];
});

/**
 * The computed datasource for the destination groups
 */
const computedDestinationGroupsDatasource = computed(() => {
    const selectLines = Groups.value.map(
        (c) =>
            ({
                value: c.ID,
                text: c.Name,
            }) as SelectLine,
    );

    const grp1: SelectLineGroup = new SelectLineGroup('racelayoutmapper.groups', selectLines);
    const grp2: SelectLineGroup = new SelectLineGroup('', []);
    grp2.lines = [
        {
            value: -1,
            text: 'racelayoutmapper.groupbelongtocategory',
        },
        {
            value: -2,
            text: 'racelayoutmapper.createindestination',
        },
        {
            value: -3,
            text: 'general.skip',
        },
    ];

    return [grp2, grp1];
});

/**
 * This will create all the categories that are not mapped to anything
 */
const createMissingCategories = () => {
    // This will set all the categories that are not mapped to anything to -2
    categoryDestinationIdMap.value = categoryDestinationIdMap.value.map((id) => (((id as number | null) ?? 0) <= 0 ? -2 : id));
};

/**
 * This will set all the groups to skip
 */
const skipAllGroups = () => {
    // Remove all the IDs from the successfully mapped
    successFullyMapped.value = successFullyMapped.value.filter((id) => !groupSourceIdMap.value.filter((id) => id > 0).includes(id));

    groupDestinationIdMap.value = groupDestinationIdMap.value.map((id) => -3);
};

// Settings
const SkipExistingContestants = ref(false);

// If we should include bib numbers in the import
const includeBibNumbers = ref(false);

// If we should include chip numbers in the import
const includeChipNumbers = ref(false);

// If we should include starting times in the import
const includeStartingTimes = ref(false);

// Set up the settings for the importer
const importSettings = ContestantImportDefaultSettings.FromJsValues({
    ColumnCaptions: [], // What we want to say in the header. Translate keys
    LockHeader: true, // Lock the header so the user cant change it ( this remove the dropdowns)
    ShowFilter: false, // Dont show the searchfilter
    ShowToolbar: false, // Dont show the toolbar
    DateFormat: 'y-m-d', // We always use the yyyy-mm-dd format
    GenderMale: 'm', // the gender is always m or f
    GenderFemale: 'f',
    GenderNeutral: 'n',
    GenerateCategories: true, // We allow for the import to create categories
    GenerateGroups: true, // We allow for the import to create groups
    HideAdvancedOptions: true, // We hide the advanced options
    ReadOnly: true, // Disable editing on the import
    SkipExistingContestants: SkipExistingContestants.value, // If we should skip existing contestants or not
    TimeFormat: 'H:m:s', // The time format we use for starting times
});

const importformat = ImportFormat.FromJsValues({
    DataFormat: 'Custom',
    DataProvider: 'EQTiming',
    DataType: 'Contestants',
    AdditionalValues: ImportAdditionalValues.FromJsValues({
        ISonenEventKey: props.externalEventData?.EventSetup?.ISonenID ?? null, // We pass the IsonenEventKey if we have it in the external data so we can use it in the import to link contestants to the source data
    }),
});

const importContent = ImportContent.FromJsValues({
    Settings: importSettings,
    Format: importformat,
    Columns: ['_col0', '_col1', '_col2', '_col3', '_col4', '_col5', '_col7', '_col6', 'id', '_status'],
    Rows: [],
});

/**
 * Helper to add the row layout to the import content
 */
const buildRowLayout = () => {
    let rowLayout: any = {
        _col0: 'Firstname',
        _col1: 'Surname',
        _col2: 'Birthday',
        _col3: 'Gender',
        _col4: 'Category',
        _col5: '_SourceCategoryName', // The name of the source category. This is only for display and doesnt do anything
        _col6: '_SourceGroupName', // This mean that we are going to create this category in the destination
        _col7: 'Group',
        id: '0',
        _status: '',
        '@ISonenID': null, // Add the Isonen ID
        '@SourceClubId': null,
        '@SourceTeamId': null,
        '@SourceParticipantId': null,
        '@SourceCategoryId': null,
        '@SourceGroupId': null,
        '@SourceContestantId': null, // We use this to check if there is multiple race contestant on the same participant
        '@RegistrarEmail': null, // We add the email of the registrar in case we need to contact them about missing data
    };

    // We add the col11 for the company/club field
    importContent.Columns.splice(6, 0, '_col11');
    rowLayout['_col11'] = 'Company';

    // We add the col12 for the club field
    importContent.Columns.splice(7, 0, '_col12');
    rowLayout['_col12'] = 'Club';

    // We need the RelayTeamName
    importContent.Columns.splice(8, 0, '_col13');
    rowLayout['_col13'] = 'RelayTeamName';

    // We add the col14 for the starting times
    if (includeStartingTimes.value) {
        importContent.Columns.splice(7, 0, '_col14');
        rowLayout['_col14'] = 'Starttime';
    }

    if (includeChipNumbers.value) {
        // Insert _col9 on the 5th position
        importContent.Columns.splice(5, 0, '_col9');
        importContent.Columns.splice(6, 0, '_col10');
        rowLayout['_col9'] = 'Chip1';
        rowLayout['_col10'] = 'Chip2';
    }

    if (includeBibNumbers.value) {
        // Insert _col8 on the 4th position
        importContent.Columns.splice(4, 0, '_col8');
        rowLayout['_col8'] = 'Startnumber';
    }

    // We add the first row with the column names so automap columns will map them correctly
    importContent.Rows.push(rowLayout);
};

/**
 * This will build and export the dataset based on an external data source like ISonen
 */
const exportDataFromExternalSource = () => {
    if (props.externalEventData == null) {
        console.error('RaceLayoutMapper: No externalEventData provided for exportDataExternal');
        return;
    }

    const sourceRaceContestants = props.externalEventData.Contestants ?? [];

    // We remove the column for groups ie = _col6 and _col7 since we dont have groups in the external data
    // Since the content of Rows[0] contains an object we will need to delete the keys for _col6 and _col7
    delete importContent.Rows[0]['_col6'];
    delete importContent.Rows[0]['_col7'];

    importContent.Columns = importContent.Columns.filter((col) => col !== '_col6' && col !== '_col7');

    // We add email to _col6
    importContent.Rows[0]['_col6'] = 'Email';
    importContent.Columns.splice(6, 0, '_col6');

    // We want to add personId/Nif IDs to the import so we can track participants properly
    importContent.Rows[0]['_col15'] = 'NIFID';
    importContent.Columns.splice(9, 0, '_col15');

    importContent.Rows[0]['_col16'] = 'BUYPASSID';
    importContent.Columns.splice(10, 0, '_col16');

    // WE add a column for Chip1
    importContent.Rows[0]['_col17'] = 'Chip1';
    importContent.Columns.splice(11, 0, '_col17');

    importContent.Rows[0]['_col18'] = 'EMITID';
    importContent.Columns.splice(12, 0, '_col18');

    // importContent.Rows[0]['_col19'] = 'LegNumber';
    // importContent.Columns.splice(13, 0, '_col19');

    // Create a map for faster lookup
    const sourceCategoryMap = new Map<number, Category>();
    sourceCategories.value.forEach((c) => {
        // TODO: Filter only the ones we will create in the destination
        sourceCategoryMap.set(c.ID, c);
    });

    // Create a map for the destination categories
    const destinationCategoryMap = new Map<number, Category>();
    Categories.value.forEach((c) => {
        destinationCategoryMap.set(c.ID, c);
    });

    const destinationGroupMap = new Map<number, Group>();
    Groups.value.forEach((g) => {
        destinationGroupMap.set(g.ID, g);
    });

    const acceptedStatuses = [1, 20];

    let rowId = -1;

    sourceRaceContestants.forEach((rc) => {
        // We ignore the ones that are not accepted i.e 1 = Registered, 20 = Carousell
        if (!acceptedStatuses.includes(rc.ContestantStatusID)) {
            return;
        }

        // We need to find the sourceCategory in the sourceCategories
        const sourceCategory = Array.from(sourceCategoryMap.values()).find((c) => c.Name === rc.CategoryName) ?? null;

        const destCategoryId =
            sourceCategory?.ID === undefined
                ? -1
                : (categoryDestinationIdMap.value[categorySourceIdMap.value.indexOf(sourceCategory?.ID)] as number);
        let destinationCategory: Category | null = destCategoryId > 0 ? (destinationCategoryMap.get(destCategoryId) ?? null) : null;

        let col4Value = '';
        // If the destination categoryID is -1 we need to map the category to age and gender
        //  - We will find the category that matches and put the name in _col3
        //      If none matches it will cause a normal error in the import
        if (destCategoryId == -1 && rc.Birthday) {
            const age = new Date().getFullYear() - rc.Birthday?.getFullYear();

            destinationCategory =
                Categories.value.find((c) => (c.AgeFrom ?? 0) <= age && (c.AgeTo ?? 150) >= age && c.Gender == rc.Gender) ?? null;

            col4Value = destinationCategory?.Name ?? '';

            // TODO: What do we do if we dont find any, or we find more than one?
        }

        // If the destination categoryID is -2 we need to create the category in the destination. We add it to a column. We will handle this in the import
        // - We  add the name to _col4 so we can show it in the UI
        else if (destCategoryId == -2) {
            col4Value = sourceCategory?.Name ?? '';
        }

        // If the destination categoryID is > 0 Then the ID is the CategoryID in the destination, so that means the category is already there.
        // - We just need to put the name in _col4 to show it.
        else if (destCategoryId > 0) {
            col4Value = destinationCategory?.Name ?? '';
        }

        let rowData: any = {
            _col0: rc.Firstname,
            _col1: rc.Surname,
            _col2: rc.Birthday?.toJSON().substring(0, 10) ?? '', // We always grab the yyyy-mm-dd format to display
            _col3: rc.Gender, // Just show the gender
            _col4: col4Value, // The destination category name
            _col5: `${rc.CategoryName}`, // This is the actual name of the Source category. This is only for display and doesnt do anything
            _col6: rc.Email ?? '', // The name of the source group. This is only for display and doesnt do anything
            // _col7: col6Value, // The destination group name
            id: (rowId--).toString(),
            _status: '',
            _col15: rc.ParticipantIDs?.find((pid) => pid.ParticipantIDTypeID === WellKnown.IDTYPE_NIF)?.Value ?? '',
            _col16: rc.ParticipantIDs?.find((pid) => pid.ParticipantIDTypeID === WellKnown.IDTYPE_BUYPASS_ID)?.Value ?? '',
            _col17: rc.Chip1 ?? '',
            _col18: rc.ParticipantIDs?.find((pid) => pid.ParticipantIDTypeID === WellKnown.IDTYPE_EMIT)?.Value ?? '',
            '@RegistrarEmail': rc.RegistrarEmail ?? '',
        };

        // We add the Bib if we have checked the box
        if (includeBibNumbers.value) {
            rowData['_col8'] = rc.Bib?.toString() ?? '';
        }

        // We add the Chip if we have checked the box
        if (includeChipNumbers.value) {
            rowData['_col9'] = rc.Chip1 ?? '';
            rowData['_col10'] = rc.Chip2 ?? '';
        }

        // Add the company/club field
        rowData['_col11'] = rc.CompanyClub ?? '';

        // We add the neccessary fields for the import
        // const contestant = sourceContestants.find((c) => c.ID === rc.ContestantID);
        rowData['@SourceClubId'] = rc?.ClubID ?? null;
        rowData['_col12'] = clubMap.value.get(rc?.ClubID ?? 0) ?? ''; // This is just to show the club name

        // TODO: We dont care about showing the team name, but we need to add the team ID to the import
        // rowData['_col13'] = !contestant?.TeamID ? '' : (Clubs.value?.find((c) => c.ID === contestant?.TeamID)?.Name ?? ''); // This is just to show the club name We dont show the name
        rowData['@SourceTeamId'] = rc?.TeamID ?? null;

        // TODO: We dont care about showing the team name, but we need to add the team ID to the import
        // rowData['_col13'] = !contestant?.TeamID ? '' : (Clubs.value?.find((c) => c.ID === contestant?.TeamID)?.Name ?? ''); // This is just to show the club name We dont show the name
        // rowData['@SourceTeamId'] = contestant?.TeamID ?? null;

        // We add RelayTeamData if it exists
        rowData['_col13'] = rc?.RelayTeamName ?? '';

        // We add the starting time if we have checked the box
        if (includeStartingTimes.value) {
            rowData['_col14'] = rc.StartingTime ? formatDateToInputFormat(rc.StartingTime, 'TIME') : '';
        }

        rowData['@ISonenID'] = rc.ISonenID ?? '';

        // We need to check if it is a relay team and then create rows for each team member that are grouped on the RelayTeamName
        if (rc.RelayTeamContestants && rc.RelayTeamContestants.length > 0) {
            // We copy the rowdata and just change the firstname, surname and birthday to the team member and we add a suffix to the name of the team member so we can identify them in the import
            for (const relayContestant of rc.RelayTeamContestants) {
                let relayRowData = JSON.parse(JSON.stringify(rowData));

                // We need to add a new unique row id. We offset it by -100000 so we can be sure it doesnt conflict with any other row id and we add the index of the team member so we can identify them in the import
                relayRowData.id = (rowId--).toString();

                relayRowData._col0 = relayContestant.Firstname;
                relayRowData._col1 = relayContestant.Surname;
                relayRowData._col2 = relayContestant.Birthday?.toJSON().substring(0, 10) ?? '';
                relayRowData._col3 = relayContestant.Gender;
                relayRowData['@SourceParticipantId'] = relayContestant.ParticipantID;

                relayRowData._col15 =
                    relayContestant.ParticipantIDs?.find((pid) => pid.ParticipantIDTypeID === WellKnown.IDTYPE_NIF)?.Value ?? '';
                relayRowData._col16 =
                    relayContestant.ParticipantIDs?.find((pid) => pid.ParticipantIDTypeID === WellKnown.IDTYPE_BUYPASS_ID)?.Value ?? '';
                relayRowData._col17 = relayContestant.Chip1 ?? '';
                relayRowData._col18 =
                    relayContestant.ParticipantIDs?.find((pid) => pid.ParticipantIDTypeID === WellKnown.IDTYPE_EMIT)?.Value ?? '';
                relayRowData['@LegNumber'] = relayContestant.LegNumber?.toString() ?? '';

                importContent.Rows.push(relayRowData);
            }
        } else {
            importContent.Rows.push(rowData);
        }
    });

    importContent.RowCount = importContent.Rows.length;

    emit('complete', importContent);
};

/**
 * This will build and export the dataset based on an exisiting event in EQTiming
 */
const exportDataFromSourceEvent = async () => {
    if (props.sourceEventId == null) {
        console.error('RaceLayoutMapper: No sourceEventId provided for exportData');
        return;
    }

    const sourceRaceContestants = await BaseObjDao.Load<RaceContestantComplete>(RaceContestantComplete, props.sourceEventId, []);
    const idarray = IntArray.FromJsValues({
        Values: sourceRaceContestants.map((rc) => rc.ContestantID),
    });
    const sourceContestants = await ContestantDao.GetBaseContestants(props.sourceEventId, idarray);

    // Create a map for faster lookup
    const sourceCategoryMap = new Map<number, Category>();
    sourceCategories.value.forEach((c) => {
        // TODO: Filter only the ones we will create in the destination
        sourceCategoryMap.set(c.ID, c);
    });

    // Create a map for the destination categories
    const destinationCategoryMap = new Map<number, Category>();
    Categories.value.forEach((c) => {
        destinationCategoryMap.set(c.ID, c);
    });

    // Create a map for faster lookup
    const sourceGroupMap = new Map<number, Group>();
    sourceGroups.value.forEach((g) => {
        // TODO: Filter only the ones we will create in the destination
        sourceGroupMap.set(g.ID, g);
    });

    const destinationGroupMap = new Map<number, Group>();
    Groups.value.forEach((g) => {
        destinationGroupMap.set(g.ID, g);
    });

    const acceptedStatuses = [1, 20];

    sourceRaceContestants.forEach((rc) => {
        const sourceCategory = sourceCategoryMap.get(rc.CategoryID ?? 0) ?? null;
        const sourceGroup = sourceGroupMap.get(rc.GroupID ?? 0) ?? null;

        if (!sourceCategory || !sourceGroup) {
            return;
        }

        // We ignore the ones that are not accepted i.e 1 = Registered, 20 = Carousell
        if (!acceptedStatuses.includes(rc.ContestantStatusID)) {
            return;
        }

        const destCategoryId = categoryDestinationIdMap.value[categorySourceIdMap.value.indexOf(sourceCategory.ID)] as number;
        let destinationCategory: Category | null = destCategoryId > 0 ? (destinationCategoryMap.get(destCategoryId) ?? null) : null;

        let col4Value = '';
        let col6Value = ''; // SourceGroupName
        // If the destination categoryID is -1 we need to map the category to age and gender
        //  - We will find the category that matches and put the name in _col3
        //      If none matches it will cause a normal error in the import
        if (destCategoryId == -1 && rc.Birthday) {
            const age = new Date().getFullYear() - rc.Birthday?.getFullYear();

            destinationCategory =
                Categories.value.find((c) => (c.AgeFrom ?? 0) <= age && (c.AgeTo ?? 150) >= age && c.Gender == rc.Gender) ?? null;

            col4Value = destinationCategory?.Name ?? '';

            // TODO: What do we do if we dont find any, or we find more than one?
        }

        // If the destination categoryID is -2 we need to create the category in the destination. We add it to a column. We will handle this in the import
        // - We  add the name to _col4 so we can show it in the UI
        else if (destCategoryId == -2) {
            col4Value = sourceCategory.Name;
        }

        // If the destination categoryID is > 0 Then the ID is the CategoryID in the destination, so that means the category is already there.
        // - We just need to put the name in _col4 to show it.
        else if (destCategoryId > 0) {
            col4Value = destinationCategory?.Name ?? '';
        }

        // Handling the groups
        let destGroupId = groupDestinationIdMap.value[groupSourceIdMap.value.indexOf(sourceGroup.ID)] as number;
        let destinationGroup: Group | null = destGroupId > 0 ? (destinationGroupMap.get(destGroupId) ?? null) : null;

        if (destGroupId == -1) {
            // if we dont have a destination group we check the destination category group connection and grab from there
            if (!destinationGroup && destinationCategory && destinationCategory.CategoryGroupConnection.length > 0) {
                destGroupId = destinationCategory.CategoryGroupConnection[0].GroupID;
                destinationGroup = destinationGroupMap.get(destGroupId) ?? null;
            }
            col6Value = destinationGroup?.Name ?? sourceGroup.Name; // We use the source group name as fallback
        } else if (destGroupId == -2) {
            // Create in destination
            col6Value = sourceGroup.Name;
        } else if (destGroupId == -3) {
            // Skip
            return;
        } else if (destGroupId > 0) {
            col6Value = destinationGroup?.Name ?? '';
        }

        let rowData: any = {
            _col0: rc.Firstname,
            _col1: rc.Surname,
            _col2: rc.Birthday?.toJSON().substring(0, 10) ?? '', // We always grab the yyyy-mm-dd format to display
            _col3: rc.Gender, // Just show the gender
            _col4: col4Value, // The destination category name
            _col5: sourceCategory.Name, // This is the actual name of the Source category. This is only for display and doesnt do anything
            _col6: sourceGroup.Name, // The name of the source group. This is only for display and doesnt do anything
            _col7: col6Value, // The destination group name
            id: rc.ID.toString(),
            _status: '',
            '@SourceParticipantId': rc.ParticipantID,
            '@SourceCategoryId': destCategoryId == -2 ? sourceCategory.ID : null,
            '@SourceGroupId': destGroupId == -2 ? sourceGroup.ID : null,
            '@SourceContestantId': rc.ContestantID, // We use this to check if there is multiple race contestant on the same participant
            '@SourceClubId': rc.ClubID ?? null,
        };

        // We add the Bib if we have checked the box
        if (includeBibNumbers.value) {
            rowData['_col8'] = rc.Bib?.toString() ?? '';
        }

        // We add the Chip if we have checked the box
        if (includeChipNumbers.value) {
            rowData['_col9'] = rc.Chip1 ?? '';
            rowData['_col10'] = rc.Chip2 ?? '';
        }

        // Add the company/club field
        rowData['_col11'] = rc.CompanyClub ?? '';

        // We add the neccessary fields for the import
        const contestant = sourceContestants.find((c) => c.ID === rc.ContestantID);
        rowData['_col12'] = !contestant?.ClubID ? '' : (Clubs.value?.find((c) => c.ID === contestant?.ClubID)?.Name ?? ''); // This is just to show the club name
        rowData['@SourceClubId'] = contestant?.ClubID ?? null;

        // TODO: We dont care about showing the team name, but we need to add the team ID to the import
        // rowData['_col13'] = !contestant?.TeamID ? '' : (Clubs.value?.find((c) => c.ID === contestant?.TeamID)?.Name ?? ''); // This is just to show the club name We dont show the name
        rowData['@SourceTeamId'] = contestant?.TeamID ?? null;

        // We add RelayTeamData if it exists
        rowData['_col13'] = rc?.RelayTeamName ?? '';

        // We add the starting time if we have checked the box
        if (includeStartingTimes.value) {
            rowData['_col14'] = rc.StartingTime ? formatDateToInputFormat(rc.StartingTime, 'TIME') : '';
        }

        importContent.Rows.push(rowData);
    });

    importContent.RowCount = importContent.Rows.length;

    emit('complete', importContent);
};

/**
 * We need to watch certain settings in the mapper to make sure the correct columns are added
 */
watch(
    [includeBibNumbers],
    () => {
        if (includeBibNumbers.value === false) {
            includeChipNumbers.value = false; // If we uncheck the bib numbers we also need to uncheck the chip numbers since they depend on the bib numbers
        }
    },
    { immediate: true },
);

/**
 * Export the mapped data
 */
const exportData = () => {
    buildRowLayout();
    if (props.externalEventData) {
        exportDataFromExternalSource();
    } else {
        exportDataFromSourceEvent();
    }
};

onMounted(() => {
    loadSourceLayout();
});
</script>

<template>
    <ContentComp
        :load-state="LoadState"
        :fill-content="true"
        class="h-100 overflow-auto p-0"
    >
        <WizardComp
            :wizardDefinition="wizardDefinition"
            :showPrevNext="true"
            :showHeader="true"
            :useGivenSpace="true"
            @complete="exportData"
            ref="wizard"
            class="h-100"
        >
            <template #wizardPage-categories>
                <div class="h-100 d-flex flex-column gap-3 mt-3">
                    <div>
                        <ButtonComp
                            button-type="Secondary"
                            icon="fa-plus"
                            @click="createMissingCategories"
                            >{{ $tm('racelayoutmapper.createmissingcategories') }}</ButtonComp
                        >
                    </div>

                    <FormLayout>
                        <div class="d-flex flex-wrap gap-2 h-100">
                            <div
                                class="w-25 w-md-25 w-sm-100 w-xs-100 me-2"
                                v-for="(cat, index) in sourceCategories"
                                :key="cat.ID"
                            >
                                <div class="d-flex flex-nowrap gap-2">
                                    <div class="ms-3 w-50 align-self-center">
                                        {{ cat.Name }}
                                    </div>
                                    <IconComp
                                        icon="fa-arrow-right"
                                        class="me-2 opacity-50 align-self-center"
                                    />
                                    <div class="flex-fill d-flex align-self-center">
                                        <DropdownSelect
                                            class="flex-fill"
                                            :class="{
                                                'focus-ring focus-ring-success focus-ring-sm': successFullyMapped.includes(cat.ID),
                                            }"
                                            v-model="categoryDestinationIdMap[index]"
                                            :data="computedDestinationCategoriesDatasource"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </FormLayout>
                </div>
            </template>
            <template #wizardPage-groups>
                <div class="h-100 d-flex flex-column gap-3 mt-3">
                    <div>
                        <ButtonComp
                            button-type="Secondary"
                            icon="fa-plus"
                            @click="skipAllGroups"
                            >{{ $tm('racelayoutmapper.skipallgroups') }}</ButtonComp
                        >
                    </div>
                    <FormLayout>
                        <div class="d-flex flex-wrap gap-2 h-100">
                            <div
                                class="w-25 w-md-25 w-sm-100 w-xs-100 me-2"
                                v-for="(group, index) in sourceGroups"
                                :key="group.ID"
                            >
                                <div class="d-flex flex-nowrap gap-2">
                                    <div class="ms-3 w-50 align-self-center">
                                        {{ group.Name }}
                                    </div>
                                    <IconComp
                                        icon="fa-arrow-right"
                                        class="me-2 opacity-50 align-self-center"
                                    />
                                    <div class="flex-fill d-flex align-self-center">
                                        <DropdownSelect
                                            class="flex-fill"
                                            :class="{
                                                'focus-ring focus-ring-success focus-ring-sm': successFullyMapped.includes(group.ID),
                                            }"
                                            v-model="groupDestinationIdMap[index]"
                                            :data="computedDestinationGroupsDatasource"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </FormLayout>
                </div>
            </template>
            <template #wizardPage-settings>
                <div class="mt-3">
                    <FormLayout>
                        <FormLayoutRow>
                            <InputBool
                                v-model="SkipExistingContestants"
                                label="racelayoutmapper.skipexistingcontestants"
                            />
                        </FormLayoutRow>
                        <FormLayoutRow>
                            <InputBool
                                v-model="includeBibNumbers"
                                label="racelayoutmapper.includebibnumbers"
                            />
                        </FormLayoutRow>

                        <Transition name="fade-inout">
                            <FormLayoutRow v-if="includeBibNumbers === true">
                                <div class="d-flex flex-nowrap">
                                    <div
                                        class="ms-3 me-2 border border-end-0 border-top-0"
                                        style="height: 12px; width: 15px"
                                    ></div>
                                    <InputBool
                                        class="flex-fill"
                                        v-model="includeChipNumbers"
                                        label="racelayoutmapper.includechipnumbers"
                                    />
                                </div>
                            </FormLayoutRow>
                        </Transition>

                        <FormLayoutRow>
                            <InputBool
                                v-model="includeStartingTimes"
                                label="racelayoutmapper.includestartingtimes"
                            />
                        </FormLayoutRow>
                    </FormLayout>
                </div>
            </template>
        </WizardComp>
    </ContentComp>
</template>
